import { defineStore } from 'pinia'
import { FEE_RECIPIENT } from '@/app/utils/constants'
import { getTcAbacusGrpcApi, getChainGrpcWasmApi } from '@/app/Services'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import {
  REFERRAL_URL_PREFIX,
  REFERRAL_CONTRACT_ADDRESS,
  REFERRAL_VOLUME_THRESHOLD_USD
} from '@/app/data/referral'
import { PromiseSettledStatus } from '@/types/enums'

const importReferralMessage = () => import('./message')

type ReferralInvitee = {
  address: string
  createdAt: string
  allTimeVolume: string
  referralPoints: string
}

type ReferralStoreState = {
  isKol: boolean
  volume: string
  isEligible: boolean
  appliedCode: string
  referrerCode: string
  feeRecipient: string
  allTimeVolume: string
  volumeThreshold: string
  urlReferralCode: string
  invitees: ReferralInvitee[]
}

const initialStateFactory = (): ReferralStoreState => ({
  volume: '0',
  invitees: [],
  isKol: false,
  appliedCode: '',
  referrerCode: '',
  isEligible: false,
  allTimeVolume: '0',
  urlReferralCode: '',
  feeRecipient: FEE_RECIPIENT,
  volumeThreshold: REFERRAL_VOLUME_THRESHOLD_USD
})

export const useReferralStore = defineStore('referral', {
  state: (): ReferralStoreState => initialStateFactory(),
  getters: {
    isReferrer: (state) => {
      return !!state.referrerCode
    },

    hasAppliedCode: (state) => {
      return !!state.appliedCode
    },

    isReferralCodeEntryAllowed: (state) => {
      return !state.referrerCode && !state.appliedCode
    },

    referralLink: (state) => {
      return `${REFERRAL_URL_PREFIX}${state.referrerCode}`
    },

    remainingVolume: (state): string => {
      const threshold = toBigNumber(state.volumeThreshold)
      const current = toBigNumber(state.volume)
      const diff = threshold.minus(current)

      return diff.gt(0) ? diff.toFixed() : '0'
    }
  },
  actions: {
    registerInvitee: lazyPiniaAction(importReferralMessage, 'registerInvitee'),

    setUrlReferralCode(urlReferralCode: string) {
      this.urlReferralCode = urlReferralCode
    },

    setReferrerEligibility({
      volume,
      isEligible,
      volumeThreshold
    }: {
      volume: string
      isEligible: boolean
      volumeThreshold: string
    }) {
      this.volume = volume
      this.isEligible = isEligible
      this.volumeThreshold = volumeThreshold
    },

    setReferralAccountStats({
      isKol,
      referrerCode,
      allTimeVolume
    }: {
      isKol: boolean
      referrerCode: string
      allTimeVolume: string
    }) {
      this.isKol = isKol
      this.referrerCode = referrerCode
      this.allTimeVolume = allTimeVolume
    },

    setInvitees(invitees: ReferralInvitee[]) {
      this.invitees = invitees
    },

    setInviteeReferrer({
      appliedCode,
      feeRecipient
    }: {
      appliedCode: string
      feeRecipient: string
    }) {
      this.appliedCode = appliedCode
      this.feeRecipient = feeRecipient
    },

    async fetchReferrerEligibility() {
      const referralStore = useReferralStore()
      const tcAbacusGrpcApi = await getTcAbacusGrpcApi()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      try {
        const response = await apiCallsRequestCache.dedupe(
          createRequestKey(CLIENT_REQUEST_KEYS.referrerEligibility, {
            account: sharedWalletStore.authZOrInjectiveAddress
          }),
          () =>
            tcAbacusGrpcApi.fetchReferrerEligibility(
              sharedWalletStore.authZOrInjectiveAddress
            )
        )

        referralStore.setReferrerEligibility({
          volume: response.volume,
          isEligible: response.isEligible,
          volumeThreshold: response.volumeThreshold
        })
      } catch {
        referralStore.setReferrerEligibility({
          volume: '0',
          isEligible: false,
          volumeThreshold: REFERRAL_VOLUME_THRESHOLD_USD
        })
      }
    },

    async fetchAccountStats() {
      const referralStore = useReferralStore()
      const wasmApi = await getChainGrpcWasmApi()
      const tcAbacusGrpcApi = await getTcAbacusGrpcApi()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const address = sharedWalletStore.authZOrInjectiveAddress

      const [codeResult, statsResult] = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.referralAccountStats, {
          address
        }),
        () =>
          Promise.allSettled([
            wasmApi.fetchSmartContractState(REFERRAL_CONTRACT_ADDRESS, {
              get_code_by_referrer: { referrer: address }
            }),
            tcAbacusGrpcApi.fetchAccountStats(address)
          ])
      )

      let isKol = false
      let referrerCode = ''
      let allTimeVolume = '0'

      if (codeResult.status === PromiseSettledStatus.Fulfilled) {
        try {
          const data = JSON.parse(
            new TextDecoder().decode(codeResult.value.data)
          ) as null | { code: string; address: string }

          referrerCode = data?.code || ''
        } catch {
          //
        }
      }

      if (statsResult.status === PromiseSettledStatus.Fulfilled) {
        isKol = statsResult.value.isKol
        allTimeVolume = statsResult.value.allTimeVolume || '0'
      }

      referralStore.setReferralAccountStats({
        isKol,
        referrerCode,
        allTimeVolume
      })
    },

    async fetchAccountInvitees() {
      const referralStore = useReferralStore()
      const tcAbacusGrpcApi = await getTcAbacusGrpcApi()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      try {
        const response = await apiCallsRequestCache.dedupe(
          createRequestKey(CLIENT_REQUEST_KEYS.accountInvitees, {
            account: sharedWalletStore.authZOrInjectiveAddress
          }),
          () =>
            tcAbacusGrpcApi.fetchAccountInvitees(
              sharedWalletStore.authZOrInjectiveAddress
            )
        )

        referralStore.setInvitees(
          response.invitees.map((invitee) => ({
            address: invitee.address,
            createdAt: invitee.createdAt,
            allTimeVolume: invitee.allTimeVolume,
            referralPoints: invitee.referralPoints
          }))
        )
      } catch {
        referralStore.setInvitees([])
      }
    },

    async fetchInviteeReferrer() {
      const referralStore = useReferralStore()
      const tcAbacusGrpcApi = await getTcAbacusGrpcApi()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      try {
        const response = await apiCallsRequestCache.dedupe(
          createRequestKey(CLIENT_REQUEST_KEYS.inviteeReferrer, {
            account: sharedWalletStore.authZOrInjectiveAddress
          }),
          () =>
            tcAbacusGrpcApi.fetchInviteeReferrer(
              sharedWalletStore.authZOrInjectiveAddress
            )
        )

        referralStore.setInviteeReferrer({
          appliedCode: response.code || '',
          feeRecipient: response.referrerAddress || FEE_RECIPIENT
        })
      } catch {
        referralStore.setInviteeReferrer({
          appliedCode: '',
          feeRecipient: FEE_RECIPIENT
        })
      }
    },

    async createReferralCode(code: string) {
      const tcAbacusGrpcApi = await getTcAbacusGrpcApi()

      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      await tcAbacusGrpcApi.createReferrerCode(
        sharedWalletStore.authZOrInjectiveAddress,
        code
      )
    }
  }
})
